import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';

import { customerSchema, type Customer } from './customer.schema';

// ── API URL constants ─────────────────────────────────────────────────────────
// DB-first path: live endpoints on bankiq-api (build #45+).
// SA-8 fix: hardcoded constant with swap-point comment (no VITE_ env var).
const CUSTOMERS_URL = '/api/customers';
// ↑ Swap to '/bankiq-customers.json' for static JSON fallback if the API is unavailable.

// Tenant ID for the Meridian Community Bank tenant (seeded by bankiq-db-seed CE Job).
// SCRUM-643: tenant derived server-side from the App ID token claim (SCRUM-102).

// ── Fetcher ───────────────────────────────────────────────────────────────────
async function fetchCustomers(): Promise<Customer[]> {
  const res = await apiFetch(CUSTOMERS_URL);
  if (!res.ok) {
    throw new Error(`GET ${CUSTOMERS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return customerSchema.array().parse(raw);
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * Fetches all customers for the demo tenant from the live bankiq-api.
 *
 * staleTime: Infinity — the dataset is stable within a session; no
 * background refetches needed. This also bounds the useMemo cost for
 * metric derivation (SA-5: ~102K evaluations run once per session).
 */
export function useIntelligenceData() {
  return useQuery<Customer[]>({
    queryKey: ['intelligence-customers'],
    queryFn: fetchCustomers,
    staleTime: Infinity,
  });
}
